export interface ModulePermission {
  title: string;
  is_enabled: boolean;
  is_visible: boolean;
}

// `export_to_joor` is granted per-account and is absent from the default permission set, so it is
// missing for virtually every user — which `usePermissionState` reports as disabled, not enabled (Trap #5).
export type ModuleKey =
  | "design"
  | "photoshoot"
  | "model_training"
  | "defacto_roar"
  | "artistic_sketch"
  | "3d_measurement"
  | "trends"
  | "brand_catalogue"
  | "admin"
  | "export_to_joor"
  | "collection_range"
  // API access (K98): the Developers section and API keys — granted per plan like every other module.
  | "api_usage"
  // The design agent's chat (beta): granted per package like every other module. Without it the user still has the
  // canvas (`/design-canvas`), never the chat.
  | "agent_chat";

export type ModulePermissions = Partial<Record<ModuleKey, ModulePermission>>;

export type PackageCode = "design_starter" | "photoshoot_starter" | "design_pro" | "design_studio" | "creative_suite" | "prod_fake_enterprice";

const STARTER_CODES: ReadonlySet<string> = new Set<string>(["design_starter", "photoshoot_starter"]);

export const isStarterPackage = (packageCode: string): boolean => STARTER_CODES.has(packageCode);

export const isFakeEnterprisePackage = (packageCode: string): boolean => packageCode === "prod_fake_enterprice";

export const isRestrictedPackage = (packageCode: string): boolean => isStarterPackage(packageCode) || isFakeEnterprisePackage(packageCode);

/**
 * "Does this user actually have this module?" — the one rule, so its three consumers cannot drift.
 *
 * It is the condition `router/AuthorizationPage.tsx` gates `requiredPermission` routes on, phrased
 * positively: a fake-enterprise package has nothing enabled, and a module is enabled only when the
 * payload says so **explicitly**. Both defaults are load-bearing:
 *
 * - `=== true`, not `!== false` — Trap #5. A module MISSING from the payload means "not enabled",
 *   never "enabled by default", so a route that requires it would bounce the user.
 * - A payload that hasn't loaded yet (`undefined`) is not evidence of anything, so it reads as
 *   not-enabled. Callers that can distinguish "still loading" from "denied" should check
 *   `permissions` themselves first — `usePermissionState` does exactly that.
 *
 * Deliberately NOT the same question as `is_visible`, which is about whether to *offer* a
 * destination and is intentionally more permissive (it is what puts users in front of the upsell).
 */
export const isModuleEnabled = (moduleKey: string, permissions: ModulePermissions | undefined, packageCode: string): boolean => {
  if (isFakeEnterprisePackage(packageCode)) return false;

  return permissions?.[moduleKey as ModuleKey]?.is_enabled === true;
};
